import {
    useCallback, useMemo,
} from 'react'
import isEmpty from 'lodash/isEmpty'
import Alert from '@mui/material/Alert'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'

import {
    useDispatch,
} from 'react-redux'
import {
    AlertTitle,
} from '@mui/material'

import {
    FormError,
} from 'app/types/common.types'
import FormErrorLabels from 'app/shared-components/FormErrorLabels'
import {
    hideMessage,
} from '../../store/formStatuses.slice'

import useStyles from './FailureMessage.styles'
import {
    getAlertTitle, getDefaultErrorMessage,
} from './FailureMessage.utils'

type Props = {
    id: string,
    errors?: FormError[] | string,
    statusCode: number,
}

const FailureMessage = ({
    id,
    errors = [],
    statusCode,
}: Readonly<Props>) => {
    const dispatch = useDispatch()

    const {
        classes,
    } = useStyles()

    const close = useCallback(() => {
        dispatch(hideMessage({
            id,
        }))
    }, [
        id,
        dispatch,
    ])

    const title = useMemo(() => {
        return getAlertTitle(statusCode)
    }, [statusCode])

    const defaultErrorMessage = useMemo(() => {
        return getDefaultErrorMessage(statusCode)
    }, [statusCode])

    return (
        <Alert
            key={`failure-${id}`}
            variant="filled"
            severity="error"
            data-testid={`failure-message-${id}`}
            icon={(
                <div className={classes.iconWrapper}>
                    <ErrorOutlineIcon
                        fontSize="inherit"
                    />
                </div>
            )}
            action={(
                <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={close}
                >
                    <CloseIcon
                        fontSize="inherit"
                        className={classes.icon}
                    />
                </IconButton>
            )}
            classes={{
                message: classes.message,
                root: classes.root,
            }}
        >
            {title && <AlertTitle className={classes.title}>{title}</AlertTitle>}

            <div className={classes.bodyText}>
                {defaultErrorMessage
                    ?? ((!isEmpty(errors) && Array.isArray(errors)) ? errors.map((error) => {
                        return (
                            <FormErrorLabels
                                key={`label-${error.message}`}
                                id={id}
                                error={error}
                            />
                        )
                    }) : (
                        <span
                            className={classes.spanWrapper}
                        >
                            {errors as string}
                        </span>
                    ))}
            </div>
        </Alert>
    )
}

export default FailureMessage
